import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { ThreeDots } from "react-loader-spinner";
import {
  FiBarChart2,
  FiArrowLeft,
  FiCheck,
  FiCreditCard,
  FiDollarSign,
  FiHome,
  FiUsers,
  FiShield,
  FiShoppingCart,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Header from "../../layouts/header";
import Footer from "../../layouts/footer";
import { getClassFiveBudget, saveClassFiveBudget } from "../../api/server";
import ClassFiveBudgetReport from "../pages/ClassFiveBudgetReport";
import {
  CLASS_FIVE_BUDGET_FORM_MAP,
  prepareClassFiveFormValues,
} from "../pages/Cclatestcourse";
import "../pages/Cclatestcourse.css";
import "./add-info-multistep.css";
import BudgetValidationFeedback from "../Common/BudgetValidationFeedback";

const BUDGET_STEPS = [
  { id: "budget_household", title: "About Your Household", description: "Tell us who is included in your household.", pages: 1, Icon: FiUsers },
  {
    id: "monthly_income",
    title: "Your Monthly Household Income",
    description: "Add monthly household income before and after deductions.",
    pages: 1,
    Icon: FiDollarSign,
  },
  {
    id: "monthly_expenses",
    title: "Monthly Expenses",
    description: "Review housing, utilities, food, transportation, and other expenses.",
    pages: 5,
    Icon: FiShoppingCart,
  },
  {
    id: "debts",
    title: "Debts",
    description: "List secured debts, unsecured debts, and tax obligations.",
    pages: 1,
    Icon: FiCreditCard,
  },
  {
    id: "legal_actions",
    title: "Legal Action",
    description: "Identify any current collection or legal activity.",
    pages: 1,
    Icon: FiShield,
  },
  {
    id: "assets",
    title: "Your Assets",
    description: "Add cash, accounts, vehicles, property, and other assets.",
    pages: 5,
    Icon: FiHome,
  },
  {
    id: "report",
    title: "View Report",
    description: "Review your completed budget analysis.",
    pages: 1,
    Icon: FiBarChart2,
  },
];

const NESTED_VALUE_KEYS = [
  "person2",
  "securedDraft",
  "unsecuredDraft",
  "vehicleDraft",
  "propertyDraft",
  "otherAssetDraft",
];

const mergeInitialValues = (config, savedValues = {}) => {
  const merged = { ...config.initialValues, ...(config === CLASS_FIVE_BUDGET_FORM_MAP.monthly_income ? prepareClassFiveFormValues("monthly_income", savedValues || {}) : savedValues || {}) };

  NESTED_VALUE_KEYS.forEach((key) => {
    if (config.initialValues[key]) {
      merged[key] = {
        ...config.initialValues[key],
        ...(savedValues?.[key] || {}),
      };
    }
  });

  return merged;
};

const AddInfoMultistep = ({
  accountId = null,
  apiType: apiTypeOverride = null,
  backToClientPath = null,
  summaryPath = null,
  embedded = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = new URLSearchParams(location.search).get("id");
  const user = useSelector((state) => state.user);
  const signedInUserId = user?.user?.data?.id || user?.data?.id;
  const apiId = accountId || userId || signedInUserId;
  const apiType = apiTypeOverride || (userId ? "admin" : "user");
  const isAdminAccount = Boolean(accountId || userId);
  const resolvedBackToClientPath = backToClientPath
    || (isAdminAccount ? `/editstudent?id=${encodeURIComponent(apiId || "")}` : null);
  const resolvedSummaryPath = summaryPath
    || (isAdminAccount ? `/budget-analysis-report?id=${encodeURIComponent(apiId || "")}` : "/dashboard");

  const [activeStep, setActiveStep] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [draftForms, setDraftForms] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const workspaceRef = useRef(null);
  const stepperRef = useRef(null);

  useEffect(() => {
    const stepper = stepperRef.current;
    const activeButton = stepper?.querySelector('[aria-current="step"]');
    if (!activeButton || stepper.scrollWidth <= stepper.clientWidth) return;
    // Move only the horizontal rail, without changing the page's vertical position.
    stepper.scrollLeft += activeButton.getBoundingClientRect().left
      - stepper.getBoundingClientRect().left
      - (stepper.clientWidth - activeButton.offsetWidth) / 2;
  }, [activeStep, loading]);

  const currentStep = BUDGET_STEPS[activeStep];
  const formConfig = currentStep.id === "report"
    ? null
    : CLASS_FIVE_BUDGET_FORM_MAP[currentStep.id];
  const isLastFormPage = activeStep === BUDGET_STEPS.length - 2
    && activePage === currentStep.pages;

  const initialValues = useMemo(
    () => formConfig ? mergeInitialValues(formConfig, draftForms[currentStep.id]) : null,
    [currentStep.id, draftForms, formConfig]
  );

  const focusWorkspace = (behavior = "smooth") => {
    window.requestAnimationFrame(() => {
      workspaceRef.current?.scrollIntoView({ behavior, block: "start" });
      workspaceRef.current?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previousRestoration; };
  }, []);

  useEffect(() => {
    // Reset restored footer position on entry and after the budget has loaded.
    const frame = window.requestAnimationFrame(() => {
      workspaceRef.current?.focus({ preventScroll: true });
      workspaceRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, apiId, apiType]);

  useEffect(() => {
    if (!apiId) return;

    let cancelled = false;
    setLoading(true);
    getClassFiveBudget(apiType, apiId)
      .then((response) => {
        if (cancelled) return;
        const payload = response.data?.data || {};
        const forms = payload.forms || {};
        setDraftForms(forms);
        setReport(payload.report || null);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.response?.data?.error || "Could not load your saved budget information.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiId, apiType]);

  const rememberValues = (formName, values) => {
    setDraftForms((current) => ({ ...current, [formName]: values }));
  };

  const moveForward = () => {
    if (activePage < currentStep.pages) {
      setActivePage((page) => page + 1);
    } else {
      const nextStep = Math.min(activeStep + 1, BUDGET_STEPS.length - 1);
      setActiveStep(nextStep);
      setMaxVisitedStep((visited) => Math.max(visited, nextStep));
      setActivePage(1);
    }
    focusWorkspace();
  };

  const moveBack = () => {
    setSaveNotice("");
    if (activeStep === BUDGET_STEPS.length - 1) {
      setActiveStep(BUDGET_STEPS.length - 2);
      setActivePage(BUDGET_STEPS[BUDGET_STEPS.length - 2].pages);
    } else if (activePage > 1) {
      setActivePage((page) => page - 1);
    } else if (activeStep > 0) {
      const previousStep = activeStep - 1;
      setActiveStep(previousStep);
      setActivePage(BUDGET_STEPS[previousStep].pages);
    }
    focusWorkspace();
  };

  const saveSection = async (formName, values) => {
    const preparedValues = prepareClassFiveFormValues(formName, values);
    const response = await saveClassFiveBudget(apiType, apiId, formName, preparedValues);
    const savedValues = response.data?.data?.forms?.[formName] || preparedValues;
    rememberValues(formName, savedValues);
    return savedValues;
  };

  const saveAllAndShowReport = async (forms) => {
    const formSteps = BUDGET_STEPS.filter((step) => step.id !== "report");
    const saveStep = (step) => {
      const config = CLASS_FIVE_BUDGET_FORM_MAP[step.id];
      const values = forms[step.id] || mergeInitialValues(config);
      return saveClassFiveBudget(
        apiType,
        apiId,
        step.id,
        prepareClassFiveFormValues(step.id, values)
      );
    };

    // Save the household section first so the shared legacy budget row exists.
    // The remaining sections write to independent fields/tables and can safely
    // run together, reducing six sequential request waits to two save phases.
    const [householdStep, ...remainingSteps] = formSteps;
    await saveStep(householdStep);
    await Promise.all(remainingSteps.map(saveStep));

    const response = await getClassFiveBudget(apiType, apiId);
    const nextReport = response.data?.data?.report;
    if (!nextReport) throw new Error("Budget report data was not returned.");

    setDraftForms(response.data.data.forms || forms);
    setReport(nextReport);
    setActiveStep(BUDGET_STEPS.length - 1);
    setMaxVisitedStep(BUDGET_STEPS.length - 1);
    setActivePage(1);
    setSaveNotice("");
    focusWorkspace();
  };

  const handleSaveAndContinue = async (values, helpers) => {
    const formName = currentStep.id;
    const nextDraftForms = { ...draftForms, [formName]: values };
    rememberValues(formName, values);
    setSaveNotice("");
    setAction(isLastFormPage ? "report" : "save");

    try {
      if (isLastFormPage) {
        await saveAllAndShowReport(nextDraftForms);
      } else {
        await saveSection(formName, values);
        setSaveNotice(`${currentStep.title} saved.`);
        moveForward();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error
        || error.message
        || (isLastFormPage ? "Could not prepare your budget report." : "Could not save this budget section.")
      );
      focusWorkspace("auto");
    } finally {
      setAction("");
      helpers.setSubmitting(false);
    }
  };

  const handleBack = (values) => {
    rememberValues(currentStep.id, values);
    moveBack();
  };

  const openStep = async (index, values) => {
    if (index === activeStep || action !== "") return;

    const reportStepIndex = BUDGET_STEPS.length - 1;
    const nextDraftForms = formConfig && values
      ? { ...draftForms, [currentStep.id]: values }
      : draftForms;

    if (formConfig && values) rememberValues(currentStep.id, values);

    if (index === reportStepIndex) {
      if (maxVisitedStep < reportStepIndex) return;
      setAction("report");
      try {
        await saveAllAndShowReport(nextDraftForms);
      } catch (error) {
        toast.error(error?.response?.data?.error || error.message || "Could not refresh your budget report.");
        focusWorkspace("auto");
      } finally {
        setAction("");
      }
      return;
    }

    setActiveStep(index);
    setActivePage(1);
    setSaveNotice("");
    focusWorkspace();
  };

  if (!apiId) {
    return (
      <div className="cc-budget-editor-page">
        {!embedded && <Header />}
        <main className="cc-budget-editor cc-budget-editor--message">
          <h1>Budget Analysis</h1>
          <p>We could not identify the account for this budget analysis.</p>
        </main>
        {!embedded && <Footer />}
      </div>
    );
  }

  const renderStepper = (values) => (
    <nav ref={stepperRef} className="cc-budget-editor__stepper" aria-label="Budget analysis progress">
      {BUDGET_STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const isComplete = index < maxVisitedStep && !isActive;
        const isReportStep = step.id === "report";
        const isAvailable = !isReportStep || maxVisitedStep === BUDGET_STEPS.length - 1;
        const StepIcon = step.Icon;
        return (
          <button
            type="button"
            key={step.id}
            className={`${isActive ? "is-active" : ""}${isComplete ? " is-complete" : ""}`}
            onClick={() => openStep(index, values)}
            disabled={!isAvailable || action !== ""}
            aria-current={isActive ? "step" : undefined}
          >
            <span className="cc-budget-editor__step-icon" aria-hidden="true">
              {isComplete ? <FiCheck /> : <StepIcon />}
            </span>
            <span className="cc-budget-editor__step-copy">
              <strong>{step.title}</strong>
              <small>{isActive ? "Current step" : isComplete ? "Completed" : `Step ${index + 1}`}</small>
            </span>
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className={`cc-budget-editor-page cc-course-page${embedded ? " cc-budget-editor-page--embedded" : ""}`}>
      {!embedded && <Header />}
      <main className="cc-budget-editor" ref={workspaceRef} tabIndex="-1">
        <header className="cc-budget-editor__intro">
          <div className="cc-budget-editor__intro-row">
            <div>
              <p>Pre-Filing Credit Counseling</p>
              <h1>Budget Analysis</h1>
              {!isAdminAccount && (
                <span>Complete each section at your own pace. You can go back and update your answers before viewing the report.</span>
              )}
            </div>
            {isAdminAccount && resolvedBackToClientPath && !embedded && (
              <Link
                className="cc-budget-editor__back-to-client"
                to={resolvedBackToClientPath}
              >
                <FiArrowLeft aria-hidden="true" />
                Back to Client
              </Link>
            )}
          </div>
        </header>

        {loading ? (
          <div className="cc-budget-editor__loading" role="status">
            <ThreeDots height="40" width="80" radius="9" color="#4fa94d" ariaLabel="loading-budget-analysis" visible />
            <p>Loading your saved budget information...</p>
          </div>
        ) : currentStep.id === "report" ? (
          <>
            {renderStepper()}
            <section className="cc-budget-editor__report">
              <ClassFiveBudgetReport
                report={report}
                onBack={moveBack}
                onDashboard={() => navigate(resolvedSummaryPath)}
                dashboardLabel={isAdminAccount ? "Go to Summary" : "Go to Dashboard"}
                loading={action !== ""}
                editorMode
              />
            </section>
          </>
        ) : (
          <Formik
            key={`budget-editor-${currentStep.id}`}
            initialValues={initialValues}
            validationSchema={formConfig.buildSchema()}
            onSubmit={handleSaveAndContinue}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <>
                {renderStepper(values)}
                <section className="cc-budget-editor__panel cc-course-content cc-course-content--class-five-form">
                  <div className="cc-budget-editor__panel-heading">
                    <div>
                      <span>Step {activeStep + 1} of {BUDGET_STEPS.length}</span>
                      <h2>{currentStep.title}</h2>
                      <p>{currentStep.description}</p>
                    </div>
                    {currentStep.pages > 1 && (
                      <strong>Part {activePage} of {currentStep.pages}</strong>
                    )}
                  </div>

                  {currentStep.pages > 1 && (
                    <div className="cc-budget-editor__substeps" aria-label={`${currentStep.title}, part ${activePage} of ${currentStep.pages}`}>
                      {Array.from({ length: currentStep.pages }, (_, index) => (
                        <button
                          type="button"
                          key={index}
                          className={`${index + 1 === activePage ? "is-active" : ""}${index + 1 < activePage ? " is-complete" : ""}`}
                          onClick={() => {
                            setActivePage(index + 1);
                            focusWorkspace();
                          }}
                          disabled={action !== "" || isSubmitting}
                          aria-label={`Open part ${index + 1} of ${currentStep.pages}`}
                          aria-current={index + 1 === activePage ? "step" : undefined}
                        >
                          <span>{index + 1}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <Form className="cc-class5-form-body cc-budget-editor__form" noValidate>
                    <formConfig.Component
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                      activeStep={activePage}
                    />

                    <BudgetValidationFeedback />
                    <div className="cc-budget-editor__actions footer-form-btn cc-has-step-actions">
                      <button
                        type="button"
                        className="btn_brnd cc-action-back"
                        onClick={() => handleBack(values)}
                        disabled={(activeStep === 0 && activePage === 1) || action !== "" || isSubmitting}
                      >
                        Back
                      </button>
                      <div>
                        <button
                          type="submit"
                          className="btn_brnd btn-next cc-action-save-step"
                          disabled={action !== "" || isSubmitting}
                        >
                          {action ? (
                            <ThreeDots height="20" width="58" radius="9" color="#fff" ariaLabel="saving-budget-step" visible />
                          ) : isLastFormPage ? "View Report" : "Save & Continue"}
                        </button>
                      </div>
                    </div>
                    {saveNotice && <p className="cc-budget-editor__save-notice" role="status">{saveNotice}</p>}
                  </Form>
                </section>
              </>
            )}
          </Formik>
        )}
      </main>
      {!embedded && <Footer />}
    </div>
  );
};

export default AddInfoMultistep;
